import {
  Add,
  CreditCard,
  Edit,
  History,
  OpenInNew,
  Payment,
  ReceiptLong,
  RequestQuote,
  Save,
  Shield,
} from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import type { Theme } from "@mui/material/styles";
import type { SxProps } from "@mui/system";
import { useEffect, useMemo, useState } from "react";
import { FeedbackSnackbar } from "../components/design/FeedbackSnackbar";
import { LoadingButton } from "../components/design/LoadingButton";
import {
  dashboardCardSx,
  dashboardPagePaddingSx,
  dashboardPageSx,
  dashboardSectionTitleSx,
} from "../components/layout/dashboardStyles";
import { PageLoadingState } from "../components/layout/PageLoadingState";
import { billingApi } from "../data/api/BillingApi";
import {
  BillingOverview,
  CompanyPaymentMethod,
  CompanyPaymentMethodInput,
} from "../data/models/Billing/BillingModels";

type BillingTab = "summary" | "methods" | "billing" | "payments";

const STATUS_LABELS: Record<string, string> = {
  trial: "Trial",
  active: "Activo",
  past_due: "Pago vencido",
  paused: "Pausado",
  cancelled: "Cancelado",
  pending: "Pendiente",
  approved: "Aprobado",
  rejected: "Rechazado",
  failed: "Fallido",
  refunded: "Reembolsado",
};

const INTERVAL_LABELS: Record<string, string> = {
  monthly: "Mensual",
  quarterly: "Trimestral",
  semiannual: "Semestral",
  annual: "Anual",
};

const emptyPaymentMethod = (): CompanyPaymentMethodInput => ({
  payerName: "",
  payerEmail: "",
  payerPhone: "",
  payerDocument: "",
  billingName: "",
  billingEmail: "",
  billingPhone: "",
  billingDocument: "",
  billingAddress: "",
  billingCity: "",
  billingCountry: "",
  paymentMethods: [],
  boldCustomerIdentifier: "",
  boldSubscriptionReference: "",
});

const emptyCard = (): CompanyPaymentMethod => ({
  id: "",
  provider: "BOLD",
  type: "card",
  brand: "",
  last4: "",
  expMonth: "",
  expYear: "",
  holderName: "",
  boldPaymentMethodId: "",
  isDefault: false,
});

const statusColor = (status?: string) => {
  if (status === "active" || status === "approved") return "success";
  if (status === "past_due" || status === "rejected" || status === "failed") return "error";
  if (status === "paused" || status === "pending" || status === "trial") return "warning";
  return "default";
};

const formatMoney = (currency?: string, amount?: number) =>
  `${currency || "COP"} ${Number(amount || 0).toLocaleString("es-CO")}`;

const formatDate = (value?: string) => {
  if (!value) return "Sin registro";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Sin registro";
  return new Intl.DateTimeFormat("es-CO", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
};

const subscriptionCardSx: SxProps<Theme> = {
  ...dashboardCardSx,
  borderRadius: 2,
  borderColor: (theme) => alpha(theme.palette.primary.main, theme.palette.mode === "dark" ? 0.28 : 0.16),
  overflow: "hidden",
  boxShadow: "none",
};

const subscriptionAccentSx: SxProps<Theme> = {
  width: 46,
  height: 46,
  borderRadius: 1.5,
  display: "grid",
  placeItems: "center",
  color: "primary.main",
  bgcolor: (theme) => alpha(theme.palette.primary.main, theme.palette.mode === "dark" ? 0.18 : 0.1),
  border: "1px solid",
  borderColor: (theme) => alpha(theme.palette.primary.main, 0.2),
  flex: "0 0 auto",
};

const summaryDetailSx: SxProps<Theme> = {
  border: "1px solid",
  borderColor: "divider",
  borderRadius: 1.5,
  bgcolor: (theme) => alpha(theme.palette.background.default, theme.palette.mode === "dark" ? 0.42 : 0.62),
  p: 1.35,
  minHeight: 74,
};

const detailLabelSx: SxProps<Theme> = {
  color: "text.secondary",
  fontSize: 11,
  fontWeight: 800,
  textTransform: "uppercase",
  letterSpacing: 0,
};

const paymentMethodsCardSx: SxProps<Theme> = {
  ...dashboardCardSx,
  borderRadius: 2,
  overflow: "hidden",
  boxShadow: "none",
};

const cardPreviewSx = (brand: string): SxProps<Theme> => {
  const key = brand.toLowerCase();
  if (key.includes("visa")) {
    return {
      background: "linear-gradient(135deg, #123a8c 0%, #2563eb 52%, #86b7ff 100%)",
      color: "#fff",
    };
  }
  if (key.includes("master")) {
    return {
      background: "linear-gradient(135deg, #111827 0%, #991b1b 48%, #f59e0b 100%)",
      color: "#fff",
    };
  }
  if (key.includes("american")) {
    return {
      background: "linear-gradient(135deg, #0f766e 0%, #14b8a6 48%, #99f6e4 100%)",
      color: "#fff",
    };
  }
  return {
    background: "linear-gradient(135deg, #1f2937 0%, #7c20ad 54%, #f97316 100%)",
    color: "#fff",
  };
};

const detectCardBrand = (value: string) => {
  const digits = value.replace(/\D/g, "");
  const firstTwo = Number(digits.slice(0, 2));
  const firstThree = Number(digits.slice(0, 3));
  const firstFour = Number(digits.slice(0, 4));
  if (/^4/.test(digits)) return "Visa";
  if ((firstTwo >= 51 && firstTwo <= 55) || (firstFour >= 2221 && firstFour <= 2720)) return "Mastercard";
  if (/^(34|37)/.test(digits)) return "American Express";
  if ((firstThree >= 300 && firstThree <= 305) || /^(36|38|39)/.test(digits)) return "Diners Club";
  return "";
};

const formatCardNumber = (value: string) =>
  value
    .replace(/\D/g, "")
    .slice(0, 19)
    .replace(/(.{4})/g, "$1 ")
    .trim();

const formatExpiry = (value: string) => {
  const digits = value.replace(/\D/g, "").slice(0, 4);
  if (digits.length <= 2) return digits;
  return `${digits.slice(0, 2)}/${digits.slice(2)}`;
};

const getMaskedCardNumber = (value: string) => {
  const digits = value.replace(/\D/g, "");
  if (!digits) return "**** **** **** ----";
  const last4 = digits.slice(-4).padEnd(4, "-");
  return `**** **** **** ${last4}`;
};

const getPaymentDraft = (overview?: BillingOverview | null): CompanyPaymentMethodInput => ({
  payerName: overview?.billing.payerName || "",
  payerEmail: overview?.billing.payerEmail || "",
  payerPhone: overview?.billing.payerPhone || "",
  payerDocument: overview?.billing.payerDocument || "",
  billingName: overview?.billing.billingName || overview?.billing.payerName || "",
  billingEmail: overview?.billing.billingEmail || overview?.billing.payerEmail || "",
  billingPhone: overview?.billing.billingPhone || overview?.billing.payerPhone || "",
  billingDocument: overview?.billing.billingDocument || overview?.billing.payerDocument || "",
  billingAddress: overview?.billing.billingAddress || "",
  billingCity: overview?.billing.billingCity || "",
  billingCountry: overview?.billing.billingCountry || overview?.countryName || "",
  paymentMethods: overview?.billing.paymentMethods || [],
  boldCustomerIdentifier: overview?.billing.boldCustomerIdentifier || "",
  boldSubscriptionReference: overview?.billing.boldSubscriptionReference || "",
});

const BillingPage = () => {
  const [tab, setTab] = useState<BillingTab>("summary");
  const [overview, setOverview] = useState<BillingOverview | null>(null);
  const [draft, setDraft] = useState<CompanyPaymentMethodInput>(emptyPaymentMethod);
  const [cardDraft, setCardDraft] = useState<CompanyPaymentMethod>(emptyCard);
  const [cardNumberInput, setCardNumberInput] = useState("");
  const [cardExpiryInput, setCardExpiryInput] = useState("");
  const [cardCvvInput, setCardCvvInput] = useState("");
  const [cardDialogOpen, setCardDialogOpen] = useState(false);
  const [billingDialogOpen, setBillingDialogOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  const billing = overview?.billing;
  const hasAssignedPlan = Boolean(billing?.planId || overview?.plan?.planId);
  const planName = billing?.planName || overview?.plan?.name || "Trial LYPRO";
  const integrationName = billing?.integrationCode || overview?.plan?.integrationCode || "Acceso inicial";
  const subscriptionStatus = hasAssignedPlan
    ? STATUS_LABELS[billing?.status || ""] || billing?.status || "Sin estado"
    : "Trial";
  const paymentMethods = billing?.paymentMethods || [];
  const paymentHistory = useMemo(() => {
    const storedHistory = billing?.paymentHistory || [];
    if (storedHistory.length) return storedHistory;
    if (!billing?.lastPaymentReference && !billing?.lastPaymentAt) return [];
    return [
      {
        id: billing.lastPaymentReference || "last-payment",
        provider: "BOLD" as const,
        reference: billing.lastPaymentReference || "Sin referencia",
        status: billing.lastPaymentStatus || billing.paymentStatus || "pending",
        amount: billing.lastPaymentAmount || 0,
        currency: billing.lastPaymentCurrency || billing.currency || "COP",
        paidAt: billing.lastPaymentAt,
      },
    ];
  }, [billing]);

  const load = async () => {
    setLoading(true);
    const result = await billingApi.getSubscription.request(null, 30_000);
    setLoading(false);
    if (!result.isSuccess || !result.data) {
      setError(result.messageError || "No se pudo cargar tu plan.");
      return;
    }
    setOverview(result.data);
    setDraft(getPaymentDraft(result.data));
  };

  useEffect(() => {
    void load();
  }, []);

  const updateDraft = (field: keyof CompanyPaymentMethodInput, value: string) =>
    setDraft((current) => ({ ...current, [field]: value }));

  const savePaymentProfile = async (nextDraft = draft) => {
    setSaving(true);
    setError("");
    setMessage("");
    const result = await billingApi.updatePaymentMethod.requestHard({ paymentMethod: nextDraft });
    setSaving(false);
    if (!result.isSuccess || !result.data) {
      setError(result.messageError || "No se pudo guardar la información de pago.");
      return false;
    }
    setOverview((current) => current ? { ...current, billing: result.data! } : current);
    setDraft(nextDraft);
    setMessage("Información de pago actualizada.");
    return true;
  };

  const openCardDialog = () => {
    setCardNumberInput("");
    setCardExpiryInput("");
    setCardCvvInput("");
    setCardDraft({
      ...emptyCard(),
      id: `card-${Date.now()}`,
      isDefault: paymentMethods.length === 0,
    });
    setCardDialogOpen(true);
  };

  const updateCardNumber = (value: string) => {
    const formattedNumber = formatCardNumber(value);
    const digits = formattedNumber.replace(/\D/g, "");
    setCardNumberInput(formattedNumber);
    setCardDraft((current) => ({
      ...current,
      brand: detectCardBrand(digits),
      last4: digits.slice(-4),
    }));
  };

  const updateCardExpiry = (value: string) => {
    const formattedExpiry = formatExpiry(value);
    const [month = "", year = ""] = formattedExpiry.split("/");
    setCardExpiryInput(formattedExpiry);
    setCardDraft((current) => ({
      ...current,
      expMonth: month,
      expYear: year.length === 2 ? `20${year}` : year,
    }));
  };

  const saveCard = async () => {
    const cleanCard = {
      ...cardDraft,
      provider: "BOLD" as const,
      type: "card" as const,
      last4: cardDraft.last4.replace(/\D/g, "").slice(-4),
      expMonth: cardDraft.expMonth.replace(/\D/g, "").slice(0, 2),
      expYear: cardDraft.expYear.replace(/\D/g, "").slice(0, 4),
      isDefault: paymentMethods.length === 0 || cardDraft.isDefault,
    };
    const nextMethods = [
      ...(draft.paymentMethods || []).map((method) => ({
        ...method,
        isDefault: cleanCard.isDefault ? false : method.isDefault,
      })),
      cleanCard,
    ];
    const nextDraft = { ...draft, paymentMethods: nextMethods };
    const saved = await savePaymentProfile(nextDraft);
    if (saved) setCardDialogOpen(false);
  };

  const saveBillingData = async () => {
    const saved = await savePaymentProfile(draft);
    if (saved) setBillingDialogOpen(false);
  };

  const renderTabs = () => (
    <ToggleButtonGroup
      value={tab}
      exclusive
      size="small"
      onChange={(_, nextValue: BillingTab | null) => {
        if (nextValue !== null) setTab(nextValue);
      }}
      sx={{
        p: 0.5,
        width: "100%",
        maxWidth: "100%",
        overflowX: "auto",
        border: "1px solid",
        borderColor: "divider",
        borderRadius: 1.5,
        bgcolor: "background.paper",
        "& .MuiToggleButtonGroup-grouped": {
          border: 0,
          borderRadius: "8px !important",
          mx: 0,
        },
        "& .MuiToggleButton-root": {
          flex: "0 0 auto",
          px: { xs: 1.25, sm: 1.75 },
          minHeight: 38,
          textTransform: "none",
          gap: 0.75,
        },
      }}
    >
      <ToggleButton value="summary"><ReceiptLong fontSize="small" />Resumen</ToggleButton>
      <ToggleButton value="methods"><CreditCard fontSize="small" />Métodos</ToggleButton>
      <ToggleButton value="billing"><RequestQuote fontSize="small" />Facturación</ToggleButton>
      <ToggleButton value="payments"><History fontSize="small" />Pagos</ToggleButton>
    </ToggleButtonGroup>
  );

  const renderSummary = () => (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: "1fr",
        gap: 1.5,
        alignItems: "stretch",
      }}
    >
      <Box>
        <Card sx={subscriptionCardSx}>
          <CardContent sx={{ p: { xs: 2, sm: 2.5 } }}>
            <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" alignItems={{ xs: "flex-start", sm: "center" }} gap={1.25}>
              <Stack direction="row" spacing={1.35} alignItems="center">
                <Box sx={subscriptionAccentSx}>
                  <ReceiptLong fontSize="small" />
                </Box>
                <Box>
                <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 800, lineHeight: 1, letterSpacing: 0 }}>
                  Suscripción LYPRO
                </Typography>
                <Typography variant="h5" sx={{ ...dashboardSectionTitleSx, fontSize: { xs: 21, sm: 25 } }}>{planName}</Typography>
                <Typography variant="body2" color="text.secondary">
                  {integrationName} · {overview?.countryName || "Colombia"} · moneda operativa {overview?.defaultCurrency || "COP"}
                </Typography>
                </Box>
              </Stack>
              <Chip
                label={subscriptionStatus}
                color={hasAssignedPlan ? statusColor(billing?.status) as any : "warning"}
                sx={{ height: 36, px: 0.75, fontWeight: 800 }}
              />
            </Stack>
            <Divider sx={{ my: 2 }} />
            {hasAssignedPlan ? (
              <Grid container spacing={1.5}>
                <Grid item xs={12} sm={4}>
                  <Box sx={summaryDetailSx}>
                    <Typography sx={detailLabelSx}>Precio</Typography>
                    <Typography variant="h6" mt={0.5}>{formatMoney(billing?.currency, billing?.amount)}</Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Box sx={summaryDetailSx}>
                    <Typography sx={detailLabelSx}>Frecuencia</Typography>
                    <Typography variant="body2" mt={0.75} fontWeight={700}>{INTERVAL_LABELS[billing?.interval || ""] || billing?.interval || "Sin frecuencia"}</Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Box sx={summaryDetailSx}>
                    <Typography sx={detailLabelSx}>Proximo cobro</Typography>
                    <Typography variant="body2" mt={0.75} fontWeight={700}>{formatDate(billing?.nextPaymentDueAt)}</Typography>
                  </Box>
                </Grid>
              </Grid>
            ) : (
              <Grid container spacing={1.5} alignItems="stretch">
                <Grid item xs={12}>
                  <Box
                    sx={{
                      border: "1px solid",
                      borderColor: (theme) => alpha(theme.palette.warning.main, theme.palette.mode === "dark" ? 0.34 : 0.26),
                      borderRadius: 1.5,
                      bgcolor: (theme) => alpha(theme.palette.warning.main, theme.palette.mode === "dark" ? 0.12 : 0.07),
                      p: { xs: 1.5, sm: 1.75 },
                    }}
                  >
                    <Typography variant="body1" fontWeight={800}>Tu compañía esta en modo trial.</Typography>
                    <Typography variant="body2" color="text.secondary" mt={0.75}>
                      LYPRO todavía no ha asignado un plan pago a esta compañía. Puedes seguir usando el acceso inicial mientras se configura la suscripción.
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Box sx={summaryDetailSx}>
                    <Typography sx={detailLabelSx}>Plan</Typography>
                    <Typography variant="body2" mt={0.75} fontWeight={700}>Sin plan asignado</Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Box sx={summaryDetailSx}>
                    <Typography sx={detailLabelSx}>Suscripción</Typography>
                    <Typography variant="body2" mt={0.75} fontWeight={700}>Pendiente</Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Box sx={summaryDetailSx}>
                    <Typography sx={detailLabelSx}>Acceso</Typography>
                    <Typography variant="body2" mt={0.75} fontWeight={700}>Inicial activo</Typography>
                  </Box>
                </Grid>
              </Grid>
            )}
            {billing?.boldPaymentLink && (
              <Button href={billing.boldPaymentLink} target="_blank" rel="noreferrer" variant="contained" startIcon={<Payment />} endIcon={<OpenInNew />} sx={{ mt: 2, borderRadius: 1.5, fontWeight: 800 }}>
                Realizar pago
              </Button>
            )}
          </CardContent>
        </Card>
      </Box>

    </Box>
  );

  const renderMethods = () => (
    <Card sx={paymentMethodsCardSx}>
      <CardContent sx={{ p: { xs: 2, sm: 2.5 } }}>
        <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" alignItems={{ xs: "flex-start", sm: "center" }} gap={1}>
          <Box>
            <Stack direction="row" gap={1} alignItems="center">
              <Box sx={{ ...subscriptionAccentSx, width: 40, height: 40 }}>
                <CreditCard fontSize="small" />
              </Box>
              <Box>
                <Typography variant="h6" sx={dashboardSectionTitleSx}>Métodos de pago</Typography>
                <Typography variant="body2" color="text.secondary">Tarjetas registradas para manejar la suscripción de los planes.</Typography>
              </Box>
            </Stack>
          </Box>
          <Button variant="contained" startIcon={<Add />} onClick={openCardDialog} sx={{ borderRadius: 1.5, fontWeight: 800, px: 2 }}>
            Agregar método
          </Button>
        </Stack>
        <Stack direction="row" gap={0.75} flexWrap="wrap" mt={1.5}>
          <Chip size="small" icon={<Shield />} label="Pago seguro" color="info" variant="outlined" />
          <Chip size="small" label="Sin CVV almacenado" variant="outlined" />
          <Chip size="small" label="Datos sensibles protegidos" variant="outlined" />
        </Stack>
        <Alert
          icon={<Shield />}
          severity="info"
          sx={{
            mt: 1.5,
            borderRadius: 1.5,
            border: "1px solid",
            borderColor: (theme) => alpha(theme.palette.info.main, theme.palette.mode === "dark" ? 0.28 : 0.18),
          }}
        >
          Los pagos se procesan de forma segura. LYPRO no guarda números completos de tarjeta ni CVV; solo almacena metadata segura como marca, últimos 4 dígitos, vencimiento y una referencia de pago.
        </Alert>
        <TableContainer sx={{ mt: 1.5, overflowX: "auto", border: "1px solid", borderColor: "divider", borderRadius: 1.5 }}>
          <Table size="small" sx={{ minWidth: 760 }}>
            <TableHead>
              <TableRow sx={{ bgcolor: (theme) => alpha(theme.palette.text.primary, theme.palette.mode === "dark" ? 0.08 : 0.035) }}>
                <TableCell>Método</TableCell>
                <TableCell>Titular</TableCell>
                <TableCell>Vencimiento</TableCell>
                <TableCell>Proveedor</TableCell>
                <TableCell>Estado</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paymentMethods.map((method) => (
                <TableRow key={method.id} hover>
                  <TableCell>
                    <Stack direction="row" gap={1} alignItems="center">
                      <Box sx={{ ...subscriptionAccentSx, width: 34, height: 34 }}>
                        <CreditCard fontSize="small" />
                      </Box>
                      <Box>
                        <Typography variant="body2" fontWeight={800}>{method.brand || "Tarjeta"} terminada en {method.last4 || "----"}</Typography>
                        <Typography variant="caption" color="text.secondary">{method.boldPaymentMethodId || "Referencia de pago pendiente"}</Typography>
                      </Box>
                    </Stack>
                  </TableCell>
                  <TableCell>{method.holderName || "Sin titular"}</TableCell>
                  <TableCell>{method.expMonth || "--"}/{method.expYear || "----"}</TableCell>
                  <TableCell><Chip size="small" label="Pago seguro" color="info" variant="outlined" /></TableCell>
                  <TableCell><Chip size="small" label={method.isDefault ? "Principal" : "Registrado"} color={method.isDefault ? "success" : "default"} /></TableCell>
                </TableRow>
              ))}
              {!paymentMethods.length && (
                <TableRow>
                  <TableCell colSpan={5}>
                    <Stack alignItems="center" spacing={1} py={4}>
                      <Box sx={{ ...subscriptionAccentSx, width: 44, height: 44 }}>
                        <CreditCard fontSize="small" />
                      </Box>
                      <Typography variant="body2" fontWeight={800}>No hay métodos de pago registrados.</Typography>
                      <Typography variant="caption" color="text.secondary" textAlign="center">
                        Agrega una tarjeta para activar los cobros de tu suscripción.
                      </Typography>
                    </Stack>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </CardContent>
    </Card>
  );

  const renderBillingData = () => (
    <Card sx={dashboardCardSx}>
      <CardContent>
        <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" alignItems={{ xs: "flex-start", sm: "center" }} gap={1}>
          <Box>
            <Typography variant="h6" sx={dashboardSectionTitleSx}>Datos para facturación</Typography>
            <Typography variant="body2" color="text.secondary">Información fiscal usada para la suscripción de LYPRO.</Typography>
          </Box>
          <Button variant="contained" startIcon={<Edit />} onClick={() => setBillingDialogOpen(true)}>Editar datos</Button>
        </Stack>
        <Divider sx={{ my: 1.5 }} />
        <Grid container spacing={1.5}>
          {[
            ["Razon social / nombre", billing?.billingName || "Sin registrar"],
            ["Email de facturación", billing?.billingEmail || "Sin registrar"],
            ["Teléfono", billing?.billingPhone || "Sin registrar"],
            ["Documento / NIT", billing?.billingDocument || "Sin registrar"],
            ["Dirección", billing?.billingAddress || "Sin registrar"],
            ["Ciudad", billing?.billingCity || "Sin registrar"],
            ["País", billing?.billingCountry || overview?.countryName || "Sin registrar"],
          ].map(([label, value]) => (
            <Grid item xs={12} md={6} key={label}>
              <Typography variant="caption" color="text.secondary">{label}</Typography>
              <Typography variant="body2" sx={{ wordBreak: "break-word" }}>{value}</Typography>
            </Grid>
          ))}
        </Grid>
      </CardContent>
    </Card>
  );

  const renderPayments = () => (
    <Card sx={dashboardCardSx}>
      <CardContent>
        <Typography variant="h6" sx={dashboardSectionTitleSx}>Historial de pagos</Typography>
        <Typography variant="body2" color="text.secondary" mb={1.5}>Consulta aquí los movimientos y comprobantes de tus pagos.</Typography>
        <TableContainer sx={{ overflowX: "auto" }}>
          <Table size="small" sx={{ minWidth: 760 }}>
            <TableHead>
              <TableRow>
                <TableCell>Fecha</TableCell>
                <TableCell>Referencia</TableCell>
                <TableCell>Pasarela</TableCell>
                <TableCell>Estado</TableCell>
                <TableCell align="right">Monto</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paymentHistory.map((item) => (
                <TableRow key={item.id} hover>
                  <TableCell>{formatDate(item.paidAt || item.createdAt)}</TableCell>
                  <TableCell>{item.reference || "Sin referencia"}</TableCell>
                  <TableCell>{item.provider ? "Pago seguro" : "—"}</TableCell>
                  <TableCell><Chip size="small" label={STATUS_LABELS[item.status] || item.status} color={statusColor(item.status) as any} /></TableCell>
                  <TableCell align="right">{formatMoney(item.currency, item.amount)}</TableCell>
                </TableRow>
              ))}
              {!paymentHistory.length && (
                <TableRow>
                  <TableCell colSpan={5}>
                    <Typography variant="body2" color="text.secondary" textAlign="center" py={2}>Todavía no hay pagos registrados.</Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </CardContent>
    </Card>
  );

  if (loading) {
    return <PageLoadingState label="Cargando la información de facturación" />;
  }

  return (
    <Box sx={dashboardPageSx}>
      <Box sx={dashboardPagePaddingSx}>
        <Stack spacing={1.5}>
          {renderTabs()}
          {tab === "summary" && renderSummary()}
          {tab === "methods" && renderMethods()}
          {tab === "billing" && renderBillingData()}
          {tab === "payments" && renderPayments()}
        </Stack>
      </Box>

      <Dialog open={cardDialogOpen} onClose={() => !saving && setCardDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Agregar método de pago</DialogTitle>
        <DialogContent dividers>
          <Box
            sx={{
              ...cardPreviewSx(cardDraft.brand),
              width: "100%",
              maxWidth: 360,
              mx: "auto",
              aspectRatio: "1.586 / 1",
              borderRadius: 2,
              p: 1.5,
              mb: 1.5,
              minHeight: 0,
              position: "relative",
              overflow: "hidden",
              boxShadow: "none",
              "&::before": {
                content: '""',
                position: "absolute",
                width: 170,
                height: 170,
                borderRadius: "50%",
                right: -46,
                top: -54,
                bgcolor: "rgba(255,255,255,0.18)",
              },
              "&::after": {
                content: '""',
                position: "absolute",
                width: 120,
                height: 120,
                borderRadius: "50%",
                right: 34,
                bottom: -56,
                bgcolor: "rgba(255,255,255,0.12)",
              },
            }}
          >
            <Stack height="100%" minHeight={0} justifyContent="space-between" position="relative" zIndex={1}>
              <Stack direction="row" justifyContent="space-between" alignItems="flex-start" gap={1}>
                <Box>
                  <Typography variant="caption" sx={{ opacity: 0.76, fontWeight: 800, letterSpacing: 0 }}>
                    LYPRO SECURE CARD
                  </Typography>
                  <Typography variant="subtitle1" fontWeight={900} mt={0.25}>
                    {cardDraft.brand || "Tarjeta"}
                  </Typography>
                </Box>
                <Chip
                  size="small"
                  label="Tokenizada"
                  sx={{ bgcolor: "rgba(255,255,255,0.18)", color: "inherit", fontWeight: 800 }}
                />
              </Stack>
              <Box>
                <Typography variant="h6" fontWeight={800} sx={{ fontFamily: "monospace", letterSpacing: 0 }}>
                  {getMaskedCardNumber(cardNumberInput)}
                </Typography>
                <Stack direction="row" justifyContent="space-between" alignItems="flex-end" gap={2} mt={2}>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="caption" sx={{ opacity: 0.72, letterSpacing: 0 }}>Titular</Typography>
                    <Typography variant="body2" fontWeight={800} noWrap>
                      {cardDraft.holderName || "Nombre del titular"}
                    </Typography>
                  </Box>
                  <Stack direction="row" gap={2} flex="0 0 auto">
                    <Box textAlign="right">
                      <Typography variant="caption" sx={{ opacity: 0.72, letterSpacing: 0 }}>Vence</Typography>
                      <Typography variant="body2" fontWeight={800}>
                        {cardExpiryInput || "--/--"}
                      </Typography>
                    </Box>
                    <Box textAlign="right">
                      <Typography variant="caption" sx={{ opacity: 0.72, letterSpacing: 0 }}>CVV</Typography>
                      <Typography variant="body2" fontWeight={800} sx={{ fontFamily: "monospace" }}>
                        {cardCvvInput ? "*".repeat(cardCvvInput.length) : "***"}
                      </Typography>
                    </Box>
                  </Stack>
                </Stack>
              </Box>
            </Stack>
          </Box>
          <Grid container spacing={1.5}>
            <Grid item xs={12} md={6}><TextField fullWidth label="Nombre en la tarjeta" value={cardDraft.holderName} onChange={(event) => setCardDraft({ ...cardDraft, holderName: event.target.value })} /></Grid>
            <Grid item xs={12} md={6}><TextField fullWidth label="Número de tarjeta" value={cardNumberInput} onChange={(event) => updateCardNumber(event.target.value)} inputProps={{ inputMode: "numeric", autoComplete: "cc-number" }} /></Grid>
            <Grid item xs={12} md={6}><TextField fullWidth label="Vencimiento" placeholder="MM/AA" value={cardExpiryInput} onChange={(event) => updateCardExpiry(event.target.value)} inputProps={{ inputMode: "numeric", autoComplete: "cc-exp" }} /></Grid>
            <Grid item xs={12} md={6}><TextField fullWidth type="password" label="CVV" value={cardCvvInput} onChange={(event) => setCardCvvInput(event.target.value.replace(/\D/g, "").slice(0, 4))} inputProps={{ inputMode: "numeric", autoComplete: "cc-csc" }} /></Grid>
            <Grid item xs={12}><TextField fullWidth label="Dirección de facturación" value={draft.billingAddress} onChange={(event) => updateDraft("billingAddress", event.target.value)} /></Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCardDialogOpen(false)} disabled={saving}>Cancelar</Button>
          <LoadingButton variant="contained" startIcon={<Save />} loading={saving} onClick={() => void saveCard()}>Guardar</LoadingButton>
        </DialogActions>
      </Dialog>

      <Dialog open={billingDialogOpen} onClose={() => !saving && setBillingDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Editar datos de facturación</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={1.5} mt={0.25}>
            <Grid item xs={12} md={6}><TextField fullWidth label="Razon social / nombre" value={draft.billingName} onChange={(event) => updateDraft("billingName", event.target.value)} /></Grid>
            <Grid item xs={12} md={6}><TextField fullWidth label="Email de facturación" value={draft.billingEmail} onChange={(event) => updateDraft("billingEmail", event.target.value)} /></Grid>
            <Grid item xs={12} md={6}><TextField fullWidth label="Teléfono" value={draft.billingPhone} onChange={(event) => updateDraft("billingPhone", event.target.value)} /></Grid>
            <Grid item xs={12} md={6}><TextField fullWidth label="Documento / NIT" value={draft.billingDocument} onChange={(event) => updateDraft("billingDocument", event.target.value)} /></Grid>
            <Grid item xs={12} md={6}><TextField fullWidth label="Dirección" value={draft.billingAddress} onChange={(event) => updateDraft("billingAddress", event.target.value)} /></Grid>
            <Grid item xs={12} md={3}><TextField fullWidth label="Ciudad" value={draft.billingCity} onChange={(event) => updateDraft("billingCity", event.target.value)} /></Grid>
            <Grid item xs={12} md={3}><TextField fullWidth label="País" value={draft.billingCountry} onChange={(event) => updateDraft("billingCountry", event.target.value)} /></Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBillingDialogOpen(false)} disabled={saving}>Cancelar</Button>
          <LoadingButton variant="contained" startIcon={<Save />} loading={saving} onClick={() => void saveBillingData()}>Guardar</LoadingButton>
        </DialogActions>
      </Dialog>

      <FeedbackSnackbar
        open={Boolean(error || message)}
        message={error || message}
        severity={error ? "error" : "success"}
        onClose={() => {
          setError("");
          setMessage("");
        }}
      />
    </Box>
  );
};

export default BillingPage;
